type ResponsiveImageConfig = {
  src: string;
  srcSet?: string;
  sizes?: string;
  width: number;
  height: number;
};

export type ImageVariant = 'hero' | 'thumb' | 'card';

function stripExt(filePath: string) {
  return filePath.replace(/\.(webp|jpg|jpeg|png)$/i, '');
}

function buildProductSrcSet(basePath: string, widths: number[]) {
  const base = stripExt(basePath);
  return widths.map((width) => `${base}-${width}w.webp ${width}w`).join(', ');
}

const IMAGE_DIMENSIONS: Record<string, { width: number; height: number; sizes: string }> = {
  '/images/header-gorsel.webp': {
    width: 1280,
    height: 853,
    sizes: '100vw',
  },
  '/images/header-footer-logo.webp': {
    width: 400,
    height: 84,
    sizes: '(max-width: 768px) 150px, 200px',
  },
  '/images/bondex.webp': {
    width: 960,
    height: 926,
    sizes: '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 665px',
  },
  '/images/bariyerli.webp': {
    width: 144,
    height: 139,
    sizes: '72px',
  },
  '/images/cift-bariyer.webp': {
    width: 144,
    height: 139,
    sizes: '72px',
  },
};

const HEADER_SRC_SETS: Record<string, { src: string; srcSet: string }> = {
  '/images/header-gorsel.webp': {
    src: '/images/header-gorsel-960w.webp',
    srcSet: '/images/header-gorsel-640w.webp 640w, /images/header-gorsel-960w.webp 960w, /images/header-gorsel-1280w.webp 1280w',
  },
  '/images/header-footer-logo.webp': {
    src: '/images/header-footer-logo-400w.webp',
    srcSet: '/images/header-footer-logo-200w.webp 200w, /images/header-footer-logo-400w.webp 400w',
  },
  '/images/bondex.webp': {
    src: '/images/bondex-720w.webp',
    srcSet: '/images/bondex-480w.webp 480w, /images/bondex-720w.webp 720w, /images/bondex.webp 960w',
  },
};

const DEFAULT_PRODUCT_RATIO = 960 / 926;

function estimateHeight(width: number) {
  return Math.round(width / DEFAULT_PRODUCT_RATIO);
}

function getVariantConfig(src: string, variant: ImageVariant): ResponsiveImageConfig {
  const header = HEADER_SRC_SETS[src];
  if (header && variant !== 'thumb') {
    const known = IMAGE_DIMENSIONS[src];
    return {
      src: header.src,
      srcSet: header.srcSet,
      sizes: known?.sizes,
      width: known?.width ?? 960,
      height: known?.height ?? estimateHeight(960),
    };
  }

  const known = IMAGE_DIMENSIONS[src];
  const base = stripExt(src);

  if (variant === 'thumb') {
    const width = 220;
    return {
      src,
      srcSet: buildProductSrcSet(src, [220]),
      sizes: '62px',
      width,
      height: estimateHeight(width),
    };
  }

  const width = known?.width ?? 960;
  const displayWidth = variant === 'card' ? 720 : 720;

  return {
    src,
    srcSet: buildProductSrcSet(src, [480, 720]),
    sizes:
      known?.sizes ??
      (variant === 'card'
        ? '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 665px'
        : '(max-width: 768px) 100vw, 560px'),
    width,
    height: known?.height ?? estimateHeight(displayWidth),
  };
}

export function getResponsiveImage(src: string, variant: ImageVariant = 'hero'): ResponsiveImageConfig | null {
  if (!src.startsWith('/images/') || !src.endsWith('.webp')) return null;
  return getVariantConfig(src, variant);
}

export function getLcpImagePreload(src: string) {
  const header = HEADER_SRC_SETS[src];
  if (header) {
    return {
      href: header.src,
      srcSet: header.srcSet,
      sizes: IMAGE_DIMENSIONS[src]?.sizes,
    };
  }

  const base = stripExt(src);
  return {
    href: src,
    srcSet: buildProductSrcSet(src, [480, 720]),
    sizes: '(max-width: 768px) 100vw, 560px',
  };
}

export function getProductCardImage(src: string, alt: string) {
  const responsive = getResponsiveImage(src, 'card');
  if (!responsive) return { src, alt };
  return { ...responsive, alt };
}
